// Imagem em tela cheia (toque na imagem do detalhe): pinça e dois toques dão zoom, um dedo arrasta com zoom, arrastar
// para baixo sem zoom fecha. Abre com a prévia de 1280 px e troca pelo original quando ele chega. Voltar e × fecham só o
// visor (openOver).
import { useEffect, useRef, useState } from "preact/hooks";
import { IconClose } from "../Icons.tsx";
import { openOver } from "./Extras.tsx";

type Gesture = { s: number; x: number; y: number; pts: Map<number, [number, number]>; d0: number; s0: number; tap: number; moved: boolean };
const MAX = 5;

export function Viewer({ src, full, alt, onClose }: { src: string; full: string; alt: string; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const img = useRef<HTMLImageElement>(null);
  const [url, setUrl] = useState(src);
  const g = useRef<Gesture>({ s: 1, x: 0, y: 0, pts: new Map(), d0: 1, s0: 1, tap: 0, moved: false });
  useEffect(() => openOver(ref.current!), []);
  useEffect(() => {
    if (full === src) return;
    const o = new Image();
    o.onload = () => setUrl(full);
    o.src = full;
    return () => void (o.onload = null);
  }, []);
  const show = () => {
    const { s, x, y } = g.current;
    img.current!.style.transform = s === 1 && !x && !y ? "" : `translate(${x}px, ${y}px) scale(${s})`;
  };
  const spread = () => {
    const [a, b] = [...g.current.pts.values()];
    return Math.hypot(a[0] - b[0], a[1] - b[1]) || 1;
  };
  const down = (e: PointerEvent) => {
    if ((e.target as Element).closest("button")) return; // o × recebe o próprio toque
    const t = g.current;
    try {
      ref.current!.setPointerCapture(e.pointerId);
    } catch {} // ponteiro sintético (testes) não tem captura
    t.pts.set(e.pointerId, [e.clientX, e.clientY]);
    t.moved = false;
    if (t.pts.size === 2) [t.d0, t.s0] = [spread(), t.s];
  };
  const move = (e: PointerEvent) => {
    const t = g.current;
    const p = t.pts.get(e.pointerId);
    if (!p) return;
    const [dx, dy] = [e.clientX - p[0], e.clientY - p[1]];
    t.pts.set(e.pointerId, [e.clientX, e.clientY]);
    if (Math.abs(dx) + Math.abs(dy) > 3) t.moved = true;
    if (t.pts.size === 2) t.s = Math.min(MAX, Math.max(1, (t.s0 * spread()) / t.d0));
    else if (t.s > 1) [t.x, t.y] = [t.x + dx, t.y + dy];
    else if (e.pointerType === "touch") t.y = Math.max(0, t.y + dy); // sem zoom: puxar para baixo fecha ao soltar
    show();
  };
  const up = (e: PointerEvent) => {
    const t = g.current;
    if (!t.pts.delete(e.pointerId) || t.pts.size) return;
    if (t.s === 1) {
      if (t.y > 120) return ref.current!.close();
      t.y = 0;
    }
    if (!t.moved) {
      // Dois toques: zoom de 2,5× no meio, ou volta ao inteiro.
      if (e.timeStamp - t.tap < 320) [t.s, t.x, t.y, t.tap] = [t.s > 1 ? 1 : 2.5, 0, 0, 0];
      else t.tap = e.timeStamp;
    }
    show();
  };
  return (
    <dialog
      ref={ref}
      class="rd-viewer"
      aria-label="Imagem em tela cheia"
      onClose={onClose}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
    >
      <img ref={img} src={url} alt={alt} draggable={false} />
      <button type="button" class="rd-x rd-viewer-x" aria-label="Fechar a imagem" onClick={() => ref.current?.close()}>
        <IconClose />
      </button>
    </dialog>
  );
}
